import type { IntlKey } from 'locales';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { ListGroup, ListGroupItem } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import type { PublicationItem } from '../model';
import type { PublicationCategory } from './model';
import { SeeMorePublicationsList } from './see-more-publications-list';

interface SeeMorePublicationsMultiCategoriesProps {
  titleKey: IntlKey;
  allKey: IntlKey;
  onClose: () => void;
  items: ReadonlyArray<PublicationItem>;
  categories: ReadonlyArray<PublicationCategory>;
}

export function SeeMorePublicationsMultiCategories({
  items,
  categories,
  titleKey,
  allKey,
  onClose,
}: SeeMorePublicationsMultiCategoriesProps) {
  const [activeTab, setActiveTab] = useState<number | null>(null);

  const changeTab = useCallback(
    (tab: number | null) => (e: MouseEvent) => {
      e.preventDefault();
      setActiveTab(tab);
    },
    [],
  );

  const selectedItems = useMemo(() => {
    if (activeTab === null) {
      return items;
    }
    return items.filter(i => i.categoryId === activeTab);
  }, [items, activeTab]);

  return (
    <div className="container">
      <div className="row">
        <div className="col-3 border-end px-0" data-e2e="see-more-categories">
          <h3 className="ms-4 mt-4 mb-3">
            <FormattedMessage id={titleKey} />
          </h3>
          <ListGroup as="div">
            <ListGroupItem
              as="a"
              key="ALL"
              action
              active={null === activeTab}
              onClick={changeTab(null)}
              data-e2e="see-more-all-category"
            >
              <FormattedMessage id={allKey} />
            </ListGroupItem>
            {categories.map(category => (
              <ListGroupItem
                className="text-nowrap pe-5"
                as="a"
                key={category.id}
                action
                active={category.id === activeTab}
                onClick={changeTab(category.id)}
                data-e2e="see-more-category"
              >
                {category.name}
              </ListGroupItem>
            ))}
          </ListGroup>
        </div>
        <div className="col-9 d-flex flex-column px-0" data-e2e="see-more-results">
          <SeeMorePublicationsList
            items={selectedItems}
            categories={activeTab === null ? categories : undefined}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
}

SeeMorePublicationsMultiCategories.displayName = 'SeeMorePublicationsMultiCategories';
